/**
 * Wave 27.4 — Employee Loans tab (2026-02-20, Dirk).
 *
 * Embeds inside EmployeeDetail. Lists every loan for the user with:
 *   • Active loans → progress bar + balance + next due
 *   • Past loans → collapsed history
 *
 * HR can create a new loan, record a repayment, change status.
 * Non-HR (self-view) can see their own loans but cannot edit.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Wallet, Plus, ChevronRight, CheckCircle2, XCircle, Pause,
  Loader2, Calendar as CalIcon, Banknote, History,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DatePickerInput from "../DatePickerInput";

const STATUS_PILL = {
  active:        "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  fully_repaid:  "bg-sky-500/15 text-sky-300 border-sky-500/30",
  written_off:   "bg-rose-500/15 text-rose-300 border-rose-500/30",
  cancelled:     "bg-white/[0.06] text-[#a0aabf] border-white/10",
  paused:        "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

const STATUS_LABEL = {
  active: "Active", fully_repaid: "Paid off", written_off: "Written off",
  cancelled: "Cancelled", paused: "Paused",
};

const ZAR = (n) =>
  "R" + (Number(n) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EmployeeLoansTab({ uid, isHr }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openLoanId, setOpenLoanId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/employees/${uid}/loans`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load loans.");
    } finally { setLoading(false); }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="hv-card p-6 text-center text-xs text-[#647087]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading loans…</div>;

  const active = items.filter((x) => x.status === "active" || x.status === "paused");
  const closed = items.filter((x) => !["active", "paused"].includes(x.status));
  const totalMonthly = active.reduce((s, l) => s + (l.monthly_installment || 0), 0);
  const totalBalance = active.reduce((s, l) => s + (l.balance || 0), 0);

  return (
    <div className="space-y-4" data-testid="loans-tab">
      {/* Summary strip — at-a-glance numbers */}
      <div className="hv-card p-4 grid grid-cols-3 gap-3">
        <Stat label="Active loans" value={active.length} testId="loans-stat-active" />
        <Stat label="Monthly deduction" value={ZAR(totalMonthly)} testId="loans-stat-monthly" />
        <Stat label="Outstanding" value={ZAR(totalBalance)} testId="loans-stat-balance" />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">Active</div>
        {isHr && (
          <button
            onClick={() => setShowCreate(true)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="loans-create-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New loan
          </button>
        )}
      </div>

      {active.length === 0 ? (
        <div className="hv-card p-6 text-center text-xs text-[#647087]" data-testid="loans-empty-active">
          No active loans.
        </div>
      ) : (
        <div className="space-y-2">
          {active.map((l) => (
            <LoanCard
              key={l.id}
              loan={l}
              isHr={isHr}
              expanded={openLoanId === l.id}
              onToggle={() => setOpenLoanId(openLoanId === l.id ? null : l.id)}
              onChange={load}
            />
          ))}
        </div>
      )}

      {closed.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087] pt-2 flex items-center gap-1.5">
            <History className="w-3 h-3" /> History
          </div>
          <div className="space-y-2">
            {closed.map((l) => (
              <LoanCard
                key={l.id}
                loan={l}
                isHr={isHr}
                expanded={openLoanId === l.id}
                onToggle={() => setOpenLoanId(openLoanId === l.id ? null : l.id)}
                onChange={load}
                muted
              />
            ))}
          </div>
        </>
      )}

      {showCreate && (
        <CreateLoanInline
          uid={uid}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="font-display text-lg text-white mt-1">{value}</div>
    </div>
  );
}

function LoanCard({ loan, isHr, expanded, onToggle, onChange, muted = false }) {
  const pct = loan.total_owed > 0 ? Math.min(100, Math.round((loan.total_paid / loan.total_owed) * 100)) : 0;
  return (
    <div className={`hv-card p-3 ${muted ? "opacity-70" : ""}`} data-testid={`loan-card-${loan.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 text-left"
        data-testid={`loan-toggle-${loan.id}`}
      >
        <Wallet className="w-4 h-4 text-[#1FB6C1] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white font-medium truncate">
              {ZAR(loan.principal)} · {loan.term_months} mo @ {loan.interest_rate_pct}%
            </span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_PILL[loan.status] || "border-white/10 text-[#a0aabf]"}`}>
              {STATUS_LABEL[loan.status] || loan.status}
            </span>
          </div>
          <div className="text-[11px] text-[#a0aabf] truncate">
            {loan.reason || "No reason recorded"}
            {loan.next_installment ? ` · Next: ${loan.next_installment.due_date}` : ""}
          </div>
          <div className="mt-2 h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1FB6C1] to-[#A78BFA]"
              style={{ width: `${pct}%` }}
              data-testid={`loan-progress-${loan.id}`}
            />
          </div>
          <div className="text-[10px] text-[#647087] mt-1 flex justify-between">
            <span>Paid {ZAR(loan.total_paid)}</span>
            <span>Balance {ZAR(loan.balance)}</span>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-[#647087] shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/[0.05] space-y-3">
          <LoanSchedule loan={loan} />
          <LoanRepayments loanId={loan.id} isHr={isHr} onAdded={onChange} />
          {isHr && loan.status === "active" && (
            <LoanActions loan={loan} onChange={onChange} />
          )}
        </div>
      )}
    </div>
  );
}

function LoanSchedule({ loan }) {
  if (!loan.schedule || loan.schedule.length === 0) return null;
  return (
    <div data-testid={`loan-schedule-${loan.id}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1.5 flex items-center gap-1">
        <CalIcon className="w-3 h-3" /> Schedule
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {loan.schedule.map((s, idx) => (
          <div key={idx} className="text-[11px] bg-white/[0.03] border border-white/5 rounded px-2 py-1 flex justify-between">
            <span className="text-[#a0aabf] font-mono">{s.due_date}</span>
            <span className="text-white">{ZAR(s.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoanRepayments({ loanId, isHr, onAdded }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/team/loans/${loanId}/repayments`);
      setItems(data.items || []);
    } catch (_) { /* noop */ }
  }, [loanId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid={`loan-repayments-${loanId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] flex items-center gap-1">
          <Banknote className="w-3 h-3" /> Repayments ({items.length})
        </div>
        {isHr && (
          <button
            onClick={() => setShowAdd((x) => !x)}
            className="text-[10px] uppercase tracking-wider text-[#1FB6C1] hover:underline"
            data-testid={`loan-repay-add-toggle-${loanId}`}
          >
            {showAdd ? "Cancel" : "+ Record"}
          </button>
        )}
      </div>
      {showAdd && (
        <RecordRepaymentForm
          loanId={loanId}
          onDone={() => { setShowAdd(false); load(); onAdded?.(); }}
        />
      )}
      {items.length === 0 ? (
        <div className="text-[11px] text-[#647087]">No repayments yet.</div>
      ) : (
        <div className="space-y-1">
          {items.map((r) => (
            <div key={r.id} className="text-[11px] flex items-center justify-between bg-white/[0.03] border border-white/5 rounded px-2 py-1">
              <div>
                <span className="text-white">{ZAR(r.amount)}</span>
                <span className="text-[#647087] ml-2">{r.paid_on}</span>
                <span className="text-[#a0aabf] ml-2">· {r.method}</span>
              </div>
              <span className="text-[#647087]">{r.recorded_by_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecordRepaymentForm({ loanId, onDone }) {
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("payroll");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount."); return; }
    setSaving(true);
    try {
      await api.post(`/team/loans/${loanId}/repayments`, {
        amount: amt, paid_on: paidOn, method, note: note || null,
      });
      toast.success("Repayment recorded");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record.");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded p-2 mb-2 space-y-2" data-testid={`repay-form-${loanId}`}>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] text-[#647087]">
          Amount (R)
          <input
            type="number" min="0" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="hv-input text-xs w-full" data-testid={`repay-amount-${loanId}`}
          />
        </label>
        <label className="text-[10px] text-[#647087]">
          Paid on
          <DatePickerInput value={paidOn} onChange={setPaidOn} testId={`repay-paid-${loanId}`} />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] text-[#647087]">
          Method
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="hv-input text-xs w-full" data-testid={`repay-method-${loanId}`}>
            <option value="payroll">Payroll deduction</option>
            <option value="cash">Cash</option>
            <option value="eft">EFT</option>
            <option value="adjustment">Adjustment</option>
          </select>
        </label>
        <label className="text-[10px] text-[#647087]">
          Note (optional)
          <input value={note} onChange={(e) => setNote(e.target.value)} className="hv-input text-xs w-full" data-testid={`repay-note-${loanId}`} />
        </label>
      </div>
      <button onClick={save} disabled={saving} className="hv-btn hv-btn-primary text-xs w-full justify-center" data-testid={`repay-save-${loanId}`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        Record repayment
      </button>
    </div>
  );
}

function LoanActions({ loan, onChange }) {
  const setStatus = async (status) => {
    if (!confirm(`Set loan to ${status}?`)) return;
    try {
      await api.patch(`/team/loans/${loan.id}`, { status });
      toast.success(`Loan marked ${status}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`loan-actions-${loan.id}`}>
      <button onClick={() => setStatus("paused")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`loan-pause-${loan.id}`}>
        <Pause className="w-3 h-3" /> Pause
      </button>
      <button onClick={() => setStatus("written_off")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`loan-writeoff-${loan.id}`}>
        <XCircle className="w-3 h-3" /> Write off
      </button>
      <button onClick={() => setStatus("cancelled")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`loan-cancel-${loan.id}`}>
        Cancel
      </button>
    </div>
  );
}

function CreateLoanInline({ uid, onClose, onCreated }) {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("0");
  const [months, setMonths] = useState("6");
  const [start, setStart] = useState("");
  const [day, setDay] = useState("25");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const p = Number(principal);
    if (!p || p <= 0) { toast.error("Principal must be positive."); return; }
    if (!start) { toast.error("Pick a start date."); return; }
    setSaving(true);
    try {
      await api.post("/team/loans", {
        user_id: uid,
        principal: p,
        interest_rate_pct: Number(rate) || 0,
        term_months: Number(months) || 1,
        start_date: start,
        deduction_day_of_month: Number(day) || 25,
        reason: reason || null,
      });
      toast.success("Loan created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create.");
    } finally { setSaving(false); }
  };

  return (
    <div className="hv-card p-4 space-y-3 border-[#1FB6C1]/30" data-testid="loan-create-form">
      <div className="font-display text-sm text-white">New loan / advance</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] text-[#647087]">
          Principal (R)
          <input type="number" min="0" step="0.01" value={principal} onChange={(e) => setPrincipal(e.target.value)} className="hv-input text-sm w-full" data-testid="loan-create-principal" />
        </label>
        <label className="text-[10px] text-[#647087]">
          Term (months)
          <input type="number" min="1" max="120" value={months} onChange={(e) => setMonths(e.target.value)} className="hv-input text-sm w-full" data-testid="loan-create-months" />
        </label>
        <label className="text-[10px] text-[#647087]">
          Interest rate (% p.a.)
          <input type="number" min="0" max="100" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} className="hv-input text-sm w-full" data-testid="loan-create-rate" />
        </label>
        <label className="text-[10px] text-[#647087]">
          Start (first installment month)
          <DatePickerInput value={start} onChange={setStart} testId="loan-create-start" />
        </label>
        <label className="text-[10px] text-[#647087]">
          Deduction day-of-month
          <input type="number" min="1" max="28" value={day} onChange={(e) => setDay(e.target.value)} className="hv-input text-sm w-full" data-testid="loan-create-day" />
        </label>
        <label className="text-[10px] text-[#647087] col-span-2">
          Reason
          <input value={reason} onChange={(e) => setReason(e.target.value)} className="hv-input text-sm w-full" placeholder="Car repair advance, school fees, …" data-testid="loan-create-reason" />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving} className="hv-btn hv-btn-primary text-xs flex-1 justify-center" data-testid="loan-create-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Create loan
        </button>
        <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="loan-create-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
